<script>
	import AttributionStats from './AttributionStats.svelte';
	import ChartNoColors from './ChartNoColors.svelte';
	import { DatePicker } from '@svelte-plugins/datepicker';
	import { attributionSalesFunnel } from '../utils/reports-api';

	const today = new Date();
	const MILLISECONDS_IN_DAY = 24 * 60 * 60 * 1000;

	let startDate = new Date(Date.now() - 7 * MILLISECONDS_IN_DAY);
	let endDate = today;
	let selectedPageType = 'all';
	let selectedDevice = 'all';

	let isOpen = false;

	$: overviewPromise = attributionSalesFunnel(
		formatDateForAPI(startDate),
		formatDateForAPI(endDate),
		selectedDevice === 'all' ? null : selectedDevice,
		selectedPageType === 'all' ? null : selectedPageType
	);

	function getChartConfig(data) {
		return {
			type: 'line',
			data: {
				labels: data?.labels || [],
				datasets:
					!data?.datasets || data.datasets.length === 0
						? []
						: [
								{
									...data.datasets[0],
									borderColor: getColor(0),
									backgroundColor: getBackgroundColor(0),
									fill: true,
									pointRadius: 4,
									pointHoverRadius: 6,
									pointHitRadius: 10,
									pointBackgroundColor: getColor(0)
								},
								{
									...data.datasets[1],
									borderColor: getColor(1),
									backgroundColor: getBackgroundColor(1),
									fill: false,
									pointRadius: 0,
									pointHoverRadius: 0,
									pointHitRadius: 0,
									hidden: true
								}
						  ]
			},
			options: {
				elements: {
					line: {
						tension: 0.4
					}
				},
				responsive: true,
				maintainAspectRatio: true,

				scales: {
					y: {
						beginAtZero: true
					}
				},
				plugins: {
					legend: {
						display: false
					},
					title: {
						display: false,
						text: 'Sales Funnel'
					},
					tooltip: {
						mode: 'index',
						intersect: false,
						callbacks: {
							label: function (context) {
								let label = context.dataset.label || '';
								if (label) {
									label += ': ';
								}
								if (context.parsed.y !== null) {
									label += Number(context.parsed.y).toFixed(2);
								}
								if (context.datasetIndex === 0 && data.datasets[1]) {
									label += ` (${Number(data.datasets[1].data[context.dataIndex] * 100).toFixed(
										2
									)}%)`;
								}
								return label;
							}
						}
					}
				},
				hover: {
					mode: 'nearest',
					intersect: true
				}
			}
		};
	}

	function formatDate(date) {
		try {
			return new Date(date).toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'short',
				day: 'numeric'
			});
		} catch (error) {
			console.error('Error formatting date:', error);
			return 'Error';
		}
	}

	function formatDateForAPI(date) {
		try {
			const adjustedDate = new Date(date);
			const timezoneOffset = adjustedDate.getTimezoneOffset() * 60000; // Convert minutes to milliseconds
			const localDate = new Date(adjustedDate.getTime() - timezoneOffset);
			return localDate.toISOString().split('T')[0];
		} catch (error) {
			console.error('Error formatting date for API:', error);
			return 'Error';
		}
	}
	$: formattedStartDate = formatDate(startDate);
	$: formattedEndDate = formatDate(endDate);

	const toggleDatePicker = () => (isOpen = !isOpen);

	const onClearDates = () => {
		startDate = new Date(Date.now() - 7 * MILLISECONDS_IN_DAY);
		endDate = new Date();
	};

	function getColor(index) {
		const colors = ['#FFB400', '#2FBFAB', '#FFE9B3', '#C1ECE6'];
		return colors[index % colors.length];
	}

	function getBackgroundColor(index) {
		const colors = ['#FFB40019', '#2FBFAB19', '#FFE9B319', '#C1ECE619'];
		return colors[index % colors.length];
	}
</script>

<section class="overview bordered-section">
	<div style="justify-content:flex-end;float:right;display:flex;gap:5px;">
		<select
			name="pageTypes"
			id="pageTypes"
			bind:value={selectedPageType}
			on:change={() => {
				overviewPromise = attributionSalesFunnel(
					formatDateForAPI(startDate),
					formatDateForAPI(endDate),
					selectedDevice === 'all' ? null : selectedDevice,
					selectedPageType === 'all' ? null : selectedPageType
				);
			}}
		>
			<option value="all">All Page Types</option>
			<option value="product_grid">Product Grid</option>
			<option value="product_details">Product Details</option>
			<option value="other">Other</option>
			<option value="landing">Landing</option>
			<option value="homepage">Homepage</option>
			<option value="category">Category</option>
			<option value="cart">Cart</option>
			<option value="404">404</option>
		</select>
		<select
			name="device"
			id="device"
			bind:value={selectedDevice}
			on:change={() => {
				overviewPromise = attributionSalesFunnel(
					formatDateForAPI(startDate),
					formatDateForAPI(endDate),
					selectedDevice === 'all' ? null : selectedDevice,
					selectedPageType === 'all' ? null : selectedPageType
				);
			}}
		>
			<option value="all">All Devices</option>
			<option value="desktop">Desktop</option>
			<option value="mobile">Mobile</option>
		</select>
		<DatePicker bind:isOpen bind:startDate bind:endDate isRange showPresets>
			<div class="date-field" on:click={toggleDatePicker} class:open={isOpen}>
				<i class="icon-calendar" />
				<div class="date">
					{formattedStartDate} - {formattedEndDate}
				</div>
				<span on:click={onClearDates}>
					<i class="os-icon-x" />
				</span>
			</div>
		</DatePicker>
	</div>

	{#await overviewPromise}
		<div class="chart" style="height: 400px;">
			<p><span class="loader" /></p>
		</div>
	{:then sectionData}
		<div style="max-width: 320px;">
			<AttributionStats
				value={sectionData.attribution}
				percentage={sectionData.attribution_percentage * 100}
			/>
		</div>

		<div class="line-chart chart-wrapper">
			<div class="chart" style="height: 400px;">
				<ChartNoColors config={getChartConfig(sectionData.attribution_line_chart)} />
			</div>
		</div>
	{:catch error}
		<p>{error}</p>
	{/await}
</section>

<style>
	.line-chart {
		margin-top: 24px;
	}
</style>
